import React, { useState, useEffect, useRef } from 'react';
import { storage } from '../firebase/config';
import { ref, getDownloadURL } from 'firebase/storage';
import CastButton from './CastButton';
import './MediaPlayer.css';

function MediaPlayer({
  currentTrack,
  isPlaying,
  onPlay,
  onPause,
  onNext,
  onPrevious,
  queueLength,
  currentIndex,
}) {
  const audioRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [loading, setLoading] = useState(false);

  // Debug: Log track object to see DisplayTitle value
  useEffect(() => {
    if (currentTrack) {
      console.log('🔍 MediaPlayer render - currentTrack:', {
        DisplayTitle: currentTrack?.DisplayTitle,
        displayTitle: currentTrack?.displayTitle,
        Title: currentTrack?.Title,
        hasDisplayTitle: !!(
          currentTrack?.DisplayTitle || currentTrack?.displayTitle
        ),
        displayTitleValue:
          currentTrack?.DisplayTitle || currentTrack?.displayTitle,
        allKeys: Object.keys(currentTrack || {}),
      });
      console.log('🔍 Full currentTrack object:', currentTrack);
      console.log(
        '🔍 What will be rendered:',
        currentTrack?.DisplayTitle ||
          currentTrack?.displayTitle ||
          'Unknown Track'
      );
    }
  }, [currentTrack]);

  // Load audio file when track changes
  useEffect(() => {
    let isMounted = true; // Track if component is still mounted

    const loadAudio = async () => {
      // Title field IS the actual filename in storage (includes .MP3 extension)
      // DisplayTitle is for display only (no extension) - do NOT use for file path
      if (!currentTrack?.Title) {
        if (isMounted) setAudioUrl(null);
        return;
      }

      const type = currentTrack.album.type || 'Albums';
      // For Albums, require filePath. For Singles, we construct from fields so filePath is optional
      if (type !== 'Singles' && !currentTrack?.album?.filePath) {
        if (isMounted) setAudioUrl(null);
        return;
      }

      const filename = currentTrack.Title;

      if (isMounted) setLoading(true);

      try {
        let audioPath;

        // Construct path from database fields
        const language = currentTrack.album.language || 'Unknown';

        console.log('🎵 Full album object:', currentTrack.album);
        console.log(
          '🎵 Track Title (filename with extension):',
          currentTrack.Title
        );
        console.log(
          '🎵 Track DisplayTitle (display name, no extension):',
          currentTrack.DisplayTitle
        );
        console.log('🎵 Using Title for file path:', filename);

        // For Singles, always construct the path correctly (filePath may be wrong)
        if (type === 'Singles') {
          // For Singles path structure (Album comes BEFORE Artist):
          // English: Singles/English/Various/Variety/Album/Mix/Song
          // Spanish (and others): Singles/Language/Various/Variety/Album/Artist/Song
          // Always construct from fields (filePath may have wrong structure)
          const genre = currentTrack.album.genre || 'Various';
          const subgenre = currentTrack.album.subgenre || 'Variety';
          const album = currentTrack.album.album || 'Unknown';
          // For English, use "Mix", for other languages use the artist field
          const artistOrMix =
            language === 'English'
              ? 'Mix'
              : currentTrack.album.artist ||
                currentTrack.album.artistName ||
                'Mix';
          // For Singles: Album comes BEFORE Artist in the path
          // Note: album.album field contains Artist, album.artist field contains Album
          audioPath = `Singles/${language}/${genre}/${subgenre}/${artistOrMix}/${album}/${filename}`;
          console.log('🎵 Constructed Singles path:', audioPath);
        } else if (currentTrack.album.filePath) {
          // For Albums: use filePath from DB if available
          // filePath from DB is like: English/Rock/Classic/Beatles/[Album]/
          let cleanPath = currentTrack.album.filePath;
          // Remove leading/trailing slashes
          cleanPath = cleanPath.replace(/^\/+|\/+$/g, '');
          // Ensure it starts with Type/
          if (!cleanPath.startsWith(`${type}/`)) {
            cleanPath = `${type}/${cleanPath}`;
          }
          audioPath = `${cleanPath}/${filename}`;
          console.log('🎵 Using filePath:', audioPath);
        } else {
          // For Albums: Construct path from individual fields
          const artist = currentTrack.album.artist || 'Unknown';
          const genre = currentTrack.album.genre || 'Unknown';
          const subgenre = currentTrack.album.subgenre || 'None';
          const album = currentTrack.album.album || 'Unknown';
          audioPath = `${type}/${language}/${genre}/${subgenre}/${artist}/${album}/${filename}`;
          console.log('🎵 Constructed Albums path:', audioPath);
        }

        console.log('🎵 Final audio path:', audioPath);

        const audioStorageRef = ref(storage, audioPath);
        const url = await getDownloadURL(audioStorageRef);

        // Only update state if component is still mounted
        if (isMounted) {
          setAudioUrl(url);
        }
      } catch (error) {
        console.error('Error loading audio:', error);
        if (isMounted) {
          setAudioUrl(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAudio();

    // Cleanup function to prevent memory leaks
    return () => {
      isMounted = false;
    };
  }, [currentTrack]);

  // Handle play/pause
  useEffect(() => {
    if (!audioRef.current || !audioUrl) return;

    if (isPlaying) {
      audioRef.current.play().catch((err) => {
        // Handle autoplay restrictions gracefully
        if (err.name === 'NotAllowedError' || err.name === 'AbortError') {
          // Autoplay was blocked - sync state by calling onPause
          console.log('⚠️ Autoplay blocked - user interaction required');
          if (onPause) onPause();
        } else {
          console.error('Error playing audio:', err);
        }
      });
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying, audioUrl, onPause]);

  // MediaSession API - Bluetooth remote & lock screen controls
  useEffect(() => {
    if (!('mediaSession' in navigator)) {
      console.log('MediaSession API not supported');
      return;
    }

    // Set up action handlers for hardware buttons
    const actionHandlers = {
      play: () => {
        console.log('🎮 Remote: Play');
        onPlay();
      },
      pause: () => {
        console.log('🎮 Remote: Pause');
        onPause();
      },
      previoustrack: () => {
        console.log('🎮 Remote: Previous');
        if (onPrevious) onPrevious();
      },
      nexttrack: () => {
        console.log('🎮 Remote: Next');
        if (onNext) onNext();
      },
      seekbackward: (details) => {
        console.log('🎮 Remote: Seek backward', details.seekOffset || 10);
        if (audioRef.current) {
          audioRef.current.currentTime = Math.max(
            0,
            audioRef.current.currentTime - (details.seekOffset || 10)
          );
        }
      },
      seekforward: (details) => {
        console.log('🎮 Remote: Seek forward', details.seekOffset || 10);
        if (audioRef.current) {
          audioRef.current.currentTime = Math.min(
            duration,
            audioRef.current.currentTime + (details.seekOffset || 10)
          );
        }
      },
      stop: () => {
        console.log('🎮 Remote: Stop');
        onPause();
      },
    };

    // Register all action handlers
    for (const [action, handler] of Object.entries(actionHandlers)) {
      try {
        navigator.mediaSession.setActionHandler(action, handler);
      } catch (error) {
        console.log(`MediaSession action '${action}' not supported`);
      }
    }

    // Cleanup
    return () => {
      for (const action of Object.keys(actionHandlers)) {
        try {
          navigator.mediaSession.setActionHandler(action, null);
        } catch (error) {}
      }
    };
  }, [onPlay, onPause, onNext, onPrevious, duration]);

  // Update MediaSession metadata when track changes
  useEffect(() => {
    if (!('mediaSession' in navigator) || !currentTrack) return;

    navigator.mediaSession.metadata = new MediaMetadata({
      title: currentTrack.DisplayTitle || currentTrack.displayTitle,
      artist: currentTrack.album?.artist || 'Unknown Artist',
      album: currentTrack.album?.album || 'Unknown Album',
      artwork: currentTrack.album?.coverUrl
        ? [
            {
              src: currentTrack.album.coverUrl,
              sizes: '512x512',
              type: 'image/jpeg',
            },
          ]
        : [],
    });

    console.log(
      '🎮 MediaSession metadata updated - DisplayTitle:',
      currentTrack.DisplayTitle
    );
  }, [currentTrack]);

  // Update MediaSession playback state
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    navigator.mediaSession.playbackState = isPlaying ? 'playing' : 'paused';
  }, [isPlaying]);

  // Listen for volume change events (from gamepad)
  useEffect(() => {
    const handleVolumeChange = (e) => {
      const delta = e.detail?.delta || 0;
      setVolume((prevVolume) => {
        const newVolume = Math.max(0, Math.min(1, prevVolume + delta));
        if (audioRef.current) {
          audioRef.current.volume = newVolume;
        }
        if (newVolume === 0) {
          setIsMuted(true);
        } else {
          setIsMuted(false);
        }
        console.log('🎮 Volume:', Math.round(newVolume * 100) + '%');

        // Show visual feedback
        showVolumeToast(newVolume);

        return newVolume;
      });
    };

    window.addEventListener('volumeChange', handleVolumeChange);
    return () => window.removeEventListener('volumeChange', handleVolumeChange);
  }, []);

  // Volume toast notification
  const showVolumeToast = (vol) => {
    // Remove existing toast
    const existing = document.querySelector('.volume-toast');
    if (existing) existing.remove();

    // Create toast
    const toast = document.createElement('div');
    toast.className = 'volume-toast';
    toast.innerHTML = `🔊 ${Math.round(vol * 100)}%`;
    toast.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0,0,0,0.8);
      color: white;
      padding: 1rem 2rem;
      border-radius: 12px;
      font-size: 1.5rem;
      font-weight: bold;
      z-index: 99999;
      pointer-events: none;
    `;
    document.body.appendChild(toast);

    // Remove after 1 second
    setTimeout(() => toast.remove(), 1000);
  };

  // Update current time
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  // Update duration when loaded
  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  // Handle seek
  const handleSeek = (e) => {
    const seekTime = parseFloat(e.target.value);
    setCurrentTime(seekTime);
    if (audioRef.current) {
      audioRef.current.currentTime = seekTime;
    }
  };

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (audioRef.current) {
      audioRef.current.volume = newVolume;
    }
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    if (audioRef.current) {
      if (isMuted) {
        audioRef.current.volume = volume;
        setIsMuted(false);
      } else {
        audioRef.current.volume = 0;
        setIsMuted(true);
      }
    }
  };

  // Format time (seconds to mm:ss)
  const formatTime = (seconds) => {
    if (isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle track end
  const handleEnded = () => {
    // Auto-play next track if available
    if (onNext) {
      onNext();
    } else {
      onPause();
    }
  };

  if (!currentTrack) {
    return (
      <div className="media-player">
        <div className="media-player-empty">
          <span>No track selected</span>
        </div>
      </div>
    );
  }

  // Only render the audio element - controls are moved to toolbar
  return (
    <audio
      ref={audioRef}
      src={audioUrl}
      onTimeUpdate={handleTimeUpdate}
      onLoadedMetadata={handleLoadedMetadata}
      onEnded={handleEnded}
      controls={false}
      playsInline
      // Enable AirPlay for Safari
      x-webkit-airplay="allow"
    />
  );
}

export default MediaPlayer;
